// src/auth.ts
var STORAGE_KEYS = {
  TOKENS: "auth:tokens",
  USER: "auth:user"
};
var AuthModule = class {
  name = "auth";
  context;
  config;
  providers = /* @__PURE__ */ new Map();
  currentUser = null;
  tokens = null;
  refreshTimeout = null;
  constructor(config = {}) {
    this.config = {
      autoRefresh: true,
      refreshBuffer: 60,
      storagePrefix: "godgpt",
      ...config
    };
  }
  /**
   * Initialize the auth module
   */
  async init(context) {
    this.context = context;
    await this.restoreSession();
    if (this.config.autoRefresh && this.tokens) {
      this.scheduleTokenRefresh();
    }
  }
  /**
   * Register an authentication provider
   */
  registerProvider(name, handler) {
    this.providers.set(name, handler);
  }
  /**
   * Check if a provider is registered
   */
  hasProvider(name) {
    return this.providers.has(name);
  }
  /**
   * Get all registered provider names
   */
  getProviders() {
    return Array.from(this.providers.keys());
  }
  /**
   * Sign in with a provider
   */
  async signIn(provider) {
    const handler = this.providers.get(provider);
    if (!handler) {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: `Provider "${provider}" is not registered`
      };
    }
    if (handler.isAvailable && !handler.isAvailable()) {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: `Provider "${provider}" is not available on this platform`
      };
    }
    try {
      this.context.events.emit("auth:signInStart", { provider });
      const result = await handler.signIn();
      if (result.success && result.tokens) {
        await this.setSession(result.tokens, result.user ?? null);
        this.context.events.emit("auth:signIn", {
          user: this.currentUser,
          provider
        });
      } else {
        this.context.events.emit("auth:signInError", {
          provider,
          error: result.message
        });
      }
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign in failed";
      this.context.events.emit("auth:signInError", { provider, error: message });
      return {
        success: false,
        state: "error",
        tokens: null,
        message,
        error: error instanceof Error ? error : new Error(message)
      };
    }
  }
  /**
   * Sign out and clear session
   */
  async signOut() {
    const provider = this.currentUser?.provider;
    if (provider) {
      const handler = this.providers.get(provider);
      if (handler?.signOut) {
        try {
          await handler.signOut();
        } catch (error) {
          console.error("Provider signOut error:", error);
        }
      }
    }
    await this.clearSession();
    this.context.events.emit("auth:signOut");
    this.config.onAuthStateChange?.(null);
  }
  /**
   * Get current access token
   */
  getAccessToken() {
    return this.tokens?.accessToken ?? null;
  }
  /**
   * Get all tokens
   */
  getTokens() {
    return this.tokens;
  }
  /**
   * Get current user info
   */
  getCurrentUser() {
    return this.currentUser;
  }
  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.tokens !== null && !this.isTokenExpired();
  }
  /**
   * Check if the current token is expired
   */
  isTokenExpired() {
    if (!this.tokens?.expiresAt) return false;
    return Date.now() >= this.tokens.expiresAt;
  }
  /**
   * Manually refresh tokens
   */
  async refreshTokens() {
    if (!this.tokens?.refreshToken || !this.config.refreshEndpoint) {
      return false;
    }
    try {
      const response = await fetch(this.config.refreshEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          refreshToken: this.tokens.refreshToken
        })
      });
      if (!response.ok) {
        throw new Error("Token refresh failed");
      }
      const data = await response.json();
      const newTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token ?? this.tokens.refreshToken,
        expiresIn: data.expires_in,
        expiresAt: Date.now() + data.expires_in * 1e3,
        tokenType: data.token_type ?? "Bearer"
      };
      await this.setSession(newTokens, this.currentUser);
      this.config.onTokenRefresh?.(newTokens);
      this.context.events.emit("auth:tokenRefresh", { tokens: newTokens });
      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      this.context.events.emit("auth:tokenRefreshError", { error });
      return false;
    }
  }
  /**
   * Update user info
   */
  async updateUser(user) {
    if (this.currentUser) {
      this.currentUser = { ...this.currentUser, ...user };
      await this.context.storage.set(STORAGE_KEYS.USER, this.currentUser);
      this.context.events.emit("auth:userUpdated", { user: this.currentUser });
    }
  }
  /**
   * Cleanup on destroy
   */
  async destroy() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    this.providers.clear();
  }
  // Private methods
  async restoreSession() {
    try {
      const [tokens, user] = await Promise.all([
        this.context.storage.get(STORAGE_KEYS.TOKENS),
        this.context.storage.get(STORAGE_KEYS.USER)
      ]);
      if (tokens) {
        this.tokens = tokens;
        this.currentUser = user;
        if (this.isTokenExpired()) {
          const refreshed = await this.refreshTokens();
          if (!refreshed) {
            await this.clearSession();
            return;
          }
        }
        this.context.events.emit("auth:sessionRestored", {
          user: this.currentUser
        });
        this.config.onAuthStateChange?.(this.currentUser);
      }
    } catch (error) {
      console.error("Failed to restore session:", error);
    }
  }
  async setSession(tokens, user) {
    if (!tokens.expiresAt && tokens.expiresIn) {
      tokens.expiresAt = Date.now() + tokens.expiresIn * 1e3;
    }
    this.tokens = tokens;
    this.currentUser = user;
    await Promise.all([
      this.context.storage.set(STORAGE_KEYS.TOKENS, tokens),
      user ? this.context.storage.set(STORAGE_KEYS.USER, user) : Promise.resolve()
    ]);
    if (this.config.autoRefresh) {
      this.scheduleTokenRefresh();
    }
    this.config.onAuthStateChange?.(user);
  }
  async clearSession() {
    this.tokens = null;
    this.currentUser = null;
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    await Promise.all([
      this.context.storage.remove(STORAGE_KEYS.TOKENS),
      this.context.storage.remove(STORAGE_KEYS.USER)
    ]);
  }
  scheduleTokenRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    if (!this.tokens?.expiresAt || !this.config.refreshEndpoint) {
      return;
    }
    const refreshBuffer = (this.config.refreshBuffer ?? 60) * 1e3;
    const timeUntilRefresh = this.tokens.expiresAt - Date.now() - refreshBuffer;
    if (timeUntilRefresh > 0) {
      this.refreshTimeout = setTimeout(() => {
        this.refreshTokens();
      }, timeUntilRefresh);
    } else {
      this.refreshTokens();
    }
  }
};

// src/providers/google.ts
function parseJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}
function extractUserFromIdToken(idToken) {
  const payload = parseJwtPayload(idToken);
  if (!payload) return void 0;
  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
    firstName: payload.given_name,
    lastName: payload.family_name,
    avatar: payload.picture,
    provider: "google",
    metadata: {
      emailVerified: payload.email_verified,
      locale: payload.locale
    }
  };
}
function createGoogleProvider(config) {
  const {
    clientId,
    redirectUri = typeof window !== "undefined" ? window.location.origin : "",
    scopes = ["profile", "email", "openid"],
    exchangeToken
  } = config;
  return {
    isAvailable() {
      return typeof window !== "undefined";
    },
    async signIn() {
      if (typeof window === "undefined") {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: "Google sign-in is only available in browser environment"
        };
      }
      const nonce = Math.random().toString(36).substring(2, 15);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "id_token code",
        scope: scopes.join(" "),
        access_type: "offline",
        prompt: "consent",
        nonce
      });
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      const popup = window.open(
        authUrl,
        "google-oauth",
        "width=500,height=600,menubar=no,toolbar=no"
      );
      if (!popup) {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: "Failed to open popup. Please enable popups and try again."
        };
      }
      return new Promise((resolve) => {
        const checkPopup = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkPopup);
              resolve({
                success: false,
                state: "cancelled",
                tokens: null,
                message: "Sign-in was cancelled"
              });
              return;
            }
            if (popup.location.href.startsWith(redirectUri)) {
              clearInterval(checkPopup);
              const url = new URL(popup.location.href.replace("#", "?"));
              const idToken = url.searchParams.get("id_token");
              const code = url.searchParams.get("code");
              const error = url.searchParams.get("error");
              popup.close();
              if (error) {
                resolve({
                  success: false,
                  state: "error",
                  tokens: null,
                  message: error
                });
                return;
              }
              if (!idToken) {
                resolve({
                  success: false,
                  state: "error",
                  tokens: null,
                  message: "No ID token received from Google"
                });
                return;
              }
              if (exchangeToken) {
                exchangeToken({ idToken, code: code ?? void 0 }).then(({ tokens, user: user2 }) => {
                  resolve({
                    success: true,
                    state: "success",
                    tokens,
                    user: user2 ?? extractUserFromIdToken(idToken)
                  });
                }).catch((err) => {
                  resolve({
                    success: false,
                    state: "error",
                    tokens: null,
                    message: err.message ?? "Token exchange failed"
                  });
                });
                return;
              }
              const user = extractUserFromIdToken(idToken);
              resolve({
                success: true,
                state: "success",
                tokens: {
                  accessToken: idToken,
                  tokenType: "Bearer",
                  expiresIn: 3600,
                  idToken
                },
                user
              });
            }
          } catch {
          }
        }, 500);
        setTimeout(() => {
          clearInterval(checkPopup);
          if (!popup.closed) {
            popup.close();
          }
          resolve({
            success: false,
            state: "error",
            tokens: null,
            message: "Sign-in timed out"
          });
        }, 5 * 60 * 1e3);
      });
    }
  };
}

// src/providers/apple.ts
function parseJwtPayload2(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}
function extractUserFromIdToken2(idToken, appleUser) {
  const payload = parseJwtPayload2(idToken);
  if (!payload) return void 0;
  return {
    id: payload.sub,
    email: appleUser?.email ?? payload.email,
    name: appleUser?.name ? `${appleUser.name.firstName ?? ""} ${appleUser.name.lastName ?? ""}`.trim() : void 0,
    firstName: appleUser?.name?.firstName,
    lastName: appleUser?.name?.lastName,
    provider: "apple",
    metadata: {
      emailVerified: payload.email_verified,
      isPrivateEmail: payload.is_private_email
    }
  };
}
function createAppleProvider(config) {
  const {
    clientId,
    redirectUri = typeof window !== "undefined" ? window.location.origin : "",
    scopes = ["name", "email"],
    exchangeToken
  } = config;
  return {
    isAvailable() {
      return typeof window !== "undefined";
    },
    async signIn() {
      if (typeof window === "undefined") {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: "Apple sign-in is only available in browser environment"
        };
      }
      const state = Math.random().toString(36).substring(2, 15);
      const nonce = Math.random().toString(36).substring(2, 15);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code id_token",
        scope: scopes.join(" "),
        response_mode: "fragment",
        state,
        nonce
      });
      const authUrl = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
      const popup = window.open(
        authUrl,
        "apple-oauth",
        "width=500,height=700,menubar=no,toolbar=no"
      );
      if (!popup) {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: "Failed to open popup. Please enable popups and try again."
        };
      }
      return new Promise((resolve) => {
        const checkPopup = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(checkPopup);
              resolve({
                success: false,
                state: "cancelled",
                tokens: null,
                message: "Sign-in was cancelled"
              });
              return;
            }
            if (popup.location.href.startsWith(redirectUri)) {
              clearInterval(checkPopup);
              const url = new URL(popup.location.href.replace("#", "?"));
              const idToken = url.searchParams.get("id_token");
              const code = url.searchParams.get("code");
              const error = url.searchParams.get("error");
              const userParam = url.searchParams.get("user");
              popup.close();
              if (error) {
                resolve({
                  success: false,
                  state: "error",
                  tokens: null,
                  message: error
                });
                return;
              }
              if (!idToken) {
                resolve({
                  success: false,
                  state: "error",
                  tokens: null,
                  message: "No ID token received from Apple"
                });
                return;
              }
              let appleUser;
              if (userParam) {
                try {
                  appleUser = JSON.parse(decodeURIComponent(userParam));
                } catch {
                }
              }
              if (exchangeToken) {
                exchangeToken({ idToken, code: code ?? void 0, user: appleUser }).then(({ tokens, user: user2 }) => {
                  resolve({
                    success: true,
                    state: "success",
                    tokens,
                    user: user2 ?? extractUserFromIdToken2(idToken, appleUser)
                  });
                }).catch((err) => {
                  resolve({
                    success: false,
                    state: "error",
                    tokens: null,
                    message: err.message ?? "Token exchange failed"
                  });
                });
                return;
              }
              const user = extractUserFromIdToken2(idToken, appleUser);
              resolve({
                success: true,
                state: "success",
                tokens: {
                  accessToken: idToken,
                  tokenType: "Bearer",
                  expiresIn: 3600,
                  idToken
                },
                user
              });
            }
          } catch {
          }
        }, 500);
        setTimeout(() => {
          clearInterval(checkPopup);
          if (!popup.closed) {
            popup.close();
          }
          resolve({
            success: false,
            state: "error",
            tokens: null,
            message: "Sign-in timed out"
          });
        }, 5 * 60 * 1e3);
      });
    }
  };
}

// src/providers/email.ts
function createEmailProvider(_config) {
  return {
    isAvailable() {
      return true;
    },
    async signIn() {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: "Email provider requires credentials. Use createEmailAuthActions for email authentication."
      };
    }
  };
}
function createEmailAuthActions(config) {
  const {
    signInEndpoint,
    signUpEndpoint,
    resetPasswordEndpoint,
    verifyEmailEndpoint,
    headers = {}
  } = config;
  const defaultHeaders = {
    "Content-Type": "application/json",
    ...headers
  };
  async function signIn(credentials) {
    try {
      const response = await fetch(signInEndpoint, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify(credentials)
      });
      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: data.message ?? data.error ?? "Sign in failed"
        };
      }
      const tokens = {
        accessToken: data.access_token ?? data.accessToken ?? data.token,
        refreshToken: data.refresh_token ?? data.refreshToken,
        expiresIn: data.expires_in ?? data.expiresIn ?? 3600,
        tokenType: data.token_type ?? data.tokenType ?? "Bearer"
      };
      const user = data.user ? {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        firstName: data.user.firstName ?? data.user.first_name,
        lastName: data.user.lastName ?? data.user.last_name,
        avatar: data.user.avatar ?? data.user.picture,
        provider: "email",
        metadata: data.user.metadata
      } : void 0;
      return {
        success: true,
        state: "success",
        tokens,
        user
      };
    } catch (error) {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: error instanceof Error ? error.message : "Sign in failed",
        error: error instanceof Error ? error : new Error("Sign in failed")
      };
    }
  }
  async function signUp(data) {
    if (!signUpEndpoint) {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: "Sign up endpoint not configured"
      };
    }
    try {
      const response = await fetch(signUpEndpoint, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify(data)
      });
      const responseData = await response.json();
      if (!response.ok) {
        return {
          success: false,
          state: "error",
          tokens: null,
          message: responseData.message ?? responseData.error ?? "Sign up failed"
        };
      }
      if (responseData.access_token || responseData.accessToken || responseData.token) {
        const tokens = {
          accessToken: responseData.access_token ?? responseData.accessToken ?? responseData.token,
          refreshToken: responseData.refresh_token ?? responseData.refreshToken,
          expiresIn: responseData.expires_in ?? responseData.expiresIn ?? 3600,
          tokenType: responseData.token_type ?? responseData.tokenType ?? "Bearer"
        };
        const user = {
          id: responseData.user?.id ?? responseData.id,
          email: data.email,
          name: data.name ?? `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim(),
          firstName: data.firstName,
          lastName: data.lastName,
          provider: "email",
          metadata: data.metadata
        };
        return {
          success: true,
          state: "success",
          tokens,
          user
        };
      }
      return {
        success: true,
        state: "success",
        tokens: null,
        message: responseData.message ?? "Sign up successful. Please verify your email."
      };
    } catch (error) {
      return {
        success: false,
        state: "error",
        tokens: null,
        message: error instanceof Error ? error.message : "Sign up failed",
        error: error instanceof Error ? error : new Error("Sign up failed")
      };
    }
  }
  async function resetPassword(email) {
    if (!resetPasswordEndpoint) {
      return {
        success: false,
        message: "Password reset endpoint not configured"
      };
    }
    try {
      const response = await fetch(resetPasswordEndpoint, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      return {
        success: response.ok,
        message: data.message ?? (response.ok ? "Password reset email sent" : "Failed to send reset email")
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to send reset email"
      };
    }
  }
  async function verifyEmail(token) {
    if (!verifyEmailEndpoint) {
      return {
        success: false,
        message: "Email verification endpoint not configured"
      };
    }
    try {
      const response = await fetch(verifyEmailEndpoint, {
        method: "POST",
        headers: defaultHeaders,
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      return {
        success: response.ok,
        message: data.message ?? (response.ok ? "Email verified" : "Verification failed")
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Verification failed"
      };
    }
  }
  return {
    signIn,
    signUp,
    resetPassword,
    verifyEmail
  };
}

export { AuthModule, createAppleProvider, createEmailAuthActions, createEmailProvider, createGoogleProvider };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map